/**
 * Bundle Upsell — bundle-upsell.js
 *
 * Handles:
 *  - Toggling the single / bundle choice UI
 *  - Size dropdowns for both products with availability checking
 *  - Syncing displayed prices when the shopper picks a different size
 *  - Bundle Add-to-Cart via a dedicated <button type="button"> (.js-bundle-atc-btn)
 *      • The theme's native submit button is hidden when "bundle" is selected
 *      • Our button is completely outside AMP's interception path — it calls
 *        /cart/add.js directly with both variant IDs, then opens the AMP drawer.
 *  - Discount is handled by Shopify automatic discounts — no code applied via JS.
 *  - Widget still displays discounted prices and savings using discount_percentage
 *    from the product metafield (display only).
 *
 * Works alongside snippets/bundle-upsell.liquid
 */

(function () {
  'use strict';

  console.log('[bundle-upsell] script loaded');

  /* ─────────────────────────────────────────────────────────
     Helpers
  ───────────────────────────────────────────────────────── */

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ─────────────────────────────────────────────────────────
     Cart API
  ───────────────────────────────────────────────────────── */

  async function addBundleToCart(widget, bundleBtn, mainVariantId, bundleVariantId) {
    bundleBtn.disabled  = true;
    bundleBtn.textContent = 'Adding…';
    widget.classList.add('bundle-upsell--loading');

    console.log(`[bundle-upsell] addBundleToCart — mainVariantId=${mainVariantId}, bundleVariantId=${bundleVariantId}`);

    try {
      // Add BOTH items to cart in one request.
      // Discount is handled by Shopify automatic discounts — no code needed here.
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [
            { id: mainVariantId,   quantity: 1 },
            { id: bundleVariantId, quantity: 1 },
          ],
        }),
      });

      const cartData = await res.json();

      if (!res.ok) {
        throw new Error(cartData.description || cartData.message || `HTTP ${res.status}`);
      }

      console.log('[bundle-upsell] /cart/add.js success:', cartData);
      console.log('[bundle-upsell] items added:', cartData.items?.map(i => `${i.title} (${i.variant_id})`));

      // Open AMP Slide Cart (refresh data first so both items show)
      openAMPCart();

    } catch (err) {
      console.error('[bundle-upsell] add to cart failed:', err);
      alert('Sorry, something went wrong adding the bundle. Please try again.');
    } finally {
      bundleBtn.disabled    = false;
      bundleBtn.textContent = 'Add Bundle to Cart';
      widget.classList.remove('bundle-upsell--loading');
    }
  }

  function openAMPCart() {
    // AMP Slide Cart (primary — confirmed app in use)
    if (typeof window.SLIDECART_UPDATE === 'function') {
      window.SLIDECART_UPDATE(function () {
        if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
      });
      console.log('[bundle-upsell] AMP: SLIDECART_UPDATE + SLIDECART_OPEN called');
      return;
    }

    // AMP Upsell Cart (newer API)
    if (window.AMP_API && typeof window.AMP_API.OPEN_CART === 'function') {
      window.AMP_API.OPEN_CART();
      console.log('[bundle-upsell] AMP: AMP_API.OPEN_CART called');
      return;
    }

    // Fallbacks for other cart drawers
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }
    const cartBtn = document.querySelector('[aria-controls="cart-drawer"], #cart-icon-bubble');
    if (cartBtn) {
      cartBtn.closest('a, button')?.click();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    window.location.href = '/cart';
  }

  /* ─────────────────────────────────────────────────────────
     BundleUpsell class
  ───────────────────────────────────────────────────────── */

  class BundleUpsell {
    constructor(container) {
      this.container       = container;
      this.choice          = 'single';
      this.mainVariantId   = null;
      this.bundleVariantId = null;

      const dataEl = container.querySelector('#bundle-upsell-data');
      if (!dataEl) {
        console.warn('[bundle-upsell] #bundle-upsell-data not found', container);
        return;
      }

      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[bundle-upsell] failed to parse config JSON:', e);
        return;
      }

      console.log('[bundle-upsell] BundleUpsell initialised — config:', this.config);
      this._init();
    }

    _init() {
      this._bindOptionClicks();
      this._bindBundleButton();
      this._initSizeSelects();
      this._hookVariantChanges();
      this._setInitialVariant();
    }

    /* ── Option click handling ── */

    _bindOptionClicks() {
      this.container.querySelectorAll('.bundle-upsell__option').forEach(optEl => {
        optEl.addEventListener('click', (e) => {
          if (e.target.closest('select')) return;
          const radio = optEl.querySelector('input[name="bundle_choice"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            this._onChoiceChange(radio.value);
          }
        });
      });

      this.container.querySelectorAll('input[name="bundle_choice"]').forEach(radio => {
        radio.addEventListener('change', () => {
          if (radio.checked) this._onChoiceChange(radio.value);
        });
      });
    }

    _onChoiceChange(choice) {
      this.choice = choice;
      console.log(`[bundle-upsell] choice changed to "${choice}"`);

      // Toggle selected state on option boxes
      const singleOpt = this.container.querySelector('.bundle-upsell__option--single');
      const bundleOpt = this.container.querySelector('.bundle-upsell__option--bundle');
      if (singleOpt) singleOpt.classList.toggle('is-selected', choice === 'single');
      if (bundleOpt) bundleOpt.classList.toggle('is-selected', choice === 'bundle');

      // Show our custom bundle button, hide the theme's submit button — or vice versa
      const bundleBtn = this.container.querySelector('.js-bundle-atc-btn');
      const form      = this._getProductForm();
      const themeBtn  = form && form.querySelector('[name="add"], button[type="submit"]:not(.js-bundle-atc-btn)');

      if (choice === 'bundle') {
        if (bundleBtn) bundleBtn.style.display = '';
        if (themeBtn)  themeBtn.style.display  = 'none';
      } else {
        if (bundleBtn) bundleBtn.style.display = 'none';
        if (themeBtn)  themeBtn.style.display  = '';
      }
    }

    /* ── Bundle button ── */

    _bindBundleButton() {
      const bundleBtn = this.container.querySelector('.js-bundle-atc-btn');
      if (!bundleBtn) {
        console.warn('[bundle-upsell] .js-bundle-atc-btn not found');
        return;
      }

      bundleBtn.addEventListener('click', () => {
        if (this.choice !== 'bundle') return;

        // Resolve variant IDs fresh from DOM
        const mainId   = this._resolveMainVariantId();
        const bundleId = this._resolveBundleVariantId();

        console.log(`[bundle-upsell] bundle button clicked — mainId=${mainId}, bundleId=${bundleId}`);

        if (!mainId || !bundleId) {
          alert('Please select a size for both products.');
          return;
        }

        if (this._isBundleSizeUnavailable()) {
          alert('The selected size is not available. Please choose another size.');
          return;
        }

        addBundleToCart(this.container, bundleBtn, mainId, bundleId);
      });
    }

    /* ── Size selects ── */

    _initSizeSelects() {
      const mainSel   = this.container.querySelector('.js-main-size');
      const bundleSel = this.container.querySelector('.js-bundle-size');

      if (mainSel) {
        mainSel.addEventListener('change', () => {
          const id = parseInt(mainSel.value, 10);
          if (!id) return;

          // Sync back to the page's hidden variant input so theme updates prices/availability
          const form = this._getProductForm();
          if (form) {
            const hiddenInput = form.querySelector('input[name="id"]');
            if (hiddenInput) {
              hiddenInput.value = id;
              hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }

          this._onVariantChange(id);
        });
      }

      if (bundleSel) {
        bundleSel.addEventListener('change', () => {
          const id = parseInt(bundleSel.value, 10);
          if (!id) return;
          this.bundleVariantId = id;
          console.log(`[bundle-upsell] bundle size changed — bundleVariantId=${id}`);
          this._checkBundleAvailability();
        });
      }
    }

    _checkBundleAvailability() {
      const bundleSel = this.container.querySelector('.js-bundle-size');
      const msg       = this.container.querySelector('.js-bundle-unavail');
      if (!bundleSel) return;

      const selectedOpt = bundleSel.options[bundleSel.selectedIndex];
      const unavailable = selectedOpt && selectedOpt.dataset.unavailable === 'true';

      bundleSel.classList.toggle('is-unavailable', unavailable);
      if (msg) msg.classList.toggle('is-visible', unavailable);
    }

    _isBundleSizeUnavailable() {
      const bundleSel = this.container.querySelector('.js-bundle-size');
      if (!bundleSel) return false;
      const opt = bundleSel.options[bundleSel.selectedIndex];
      return opt && opt.dataset.unavailable === 'true';
    }

    /* ── Variant resolution ── */

    _resolveMainVariantId() {
      const mainSel  = this.container.querySelector('.js-main-size');
      const fromSel  = mainSel && parseInt(mainSel.value, 10);
      if (fromSel) return fromSel;

      const form      = this._getProductForm();
      const formInput = form && form.querySelector('input[name="id"]');
      const fromForm  = formInput && parseInt(formInput.value, 10);
      if (fromForm) return fromForm;

      return this.mainVariantId;
    }

    _resolveBundleVariantId() {
      const bundleSel = this.container.querySelector('.js-bundle-size');
      const fromSel   = bundleSel && parseInt(bundleSel.value, 10);
      if (fromSel) return fromSel;

      if (this.bundleVariantId) return this.bundleVariantId;

      // Fallback: first available variant from config
      try {
        const first = (this.config.bundleVariants || []).find(v => v.available)
                   || (this.config.bundleVariants || [])[0];
        if (first) return first.id;
      } catch (_) { /* ignore */ }

      return null;
    }

    /* ── Variant change detection ── */

    _setInitialVariant() {
      const form = this._getProductForm();
      if (!form) {
        console.warn('[bundle-upsell] _setInitialVariant: product form not found');
        return;
      }
      const input = form.querySelector('input[name="id"]');
      if (input && input.value) {
        this._onVariantChange(parseInt(input.value, 10));
      } else {
        const first = (this.config.mainVariants || []).find(v => v.available) || (this.config.mainVariants || [])[0];
        if (first) this._onVariantChange(first.id);
      }
    }

    _hookVariantChanges() {
      const form = this._getProductForm();

      if (form) {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          new MutationObserver(() => {
            const id = parseInt(input.value, 10);
            if (id && id !== this.mainVariantId) this._onVariantChange(id);
          }).observe(input, { attributes: true, attributeFilter: ['value'] });

          input.addEventListener('change', () => {
            const id = parseInt(input.value, 10);
            if (id && id !== this.mainVariantId) this._onVariantChange(id);
          });
        }
      }

      document.addEventListener('variant:change', (e) => {
        const v = e.detail && (e.detail.variant || e.detail);
        if (v && v.id && v.id !== this.mainVariantId) this._onVariantChange(v.id);
      });

      document.addEventListener('shopify:section:load', () => this._setInitialVariant());

      // Polling fallback — gives up once a variant is detected
      const pollTimer = setInterval(() => {
        const f = this._getProductForm();
        if (!f) return;
        const inp = f.querySelector('input[name="id"]');
        if (!inp) return;
        const id = parseInt(inp.value, 10);
        if (id && id !== this.mainVariantId) this._onVariantChange(id);
        if (this.mainVariantId) clearInterval(pollTimer);
      }, 300);

      setTimeout(() => clearInterval(pollTimer), 10000);
    }

    _onVariantChange(variantId) {
      this.mainVariantId = variantId;
      console.log(`[bundle-upsell] main variant changed — id=${variantId}`);

      const mainVariant = (this.config.mainVariants || []).find(v => v.id === variantId);
      if (!mainVariant) return;

      // Auto-match bundle variant by option1 (size)
      const sizeValue = mainVariant.option1;
      const matched   = (this.config.bundleVariants || []).find(v => v.option1 === sizeValue);
      this.bundleVariantId = matched ? matched.id : ((this.config.bundleVariants || [])[0] || {}).id;

      console.log(`[bundle-upsell] auto-matched bundleVariantId=${this.bundleVariantId} (size "${sizeValue}")`);

      // Sync main size select
      const mainSel = this.container.querySelector('.js-main-size');
      if (mainSel) mainSel.value = variantId;

      // Sync bundle size select and check availability
      const bundleSel = this.container.querySelector('.js-bundle-size');
      if (bundleSel && this.bundleVariantId) {
        bundleSel.value = this.bundleVariantId;
        this._checkBundleAvailability();
      }

      this._updatePrices(mainVariant, matched || (this.config.bundleVariants || [])[0]);
    }

    /* ── Price updates ── */

    _updatePrices(mainVariant, bundleVariant) {
      if (!bundleVariant) return;

      const disc       = this.config.discountPercentage / 100;
      const mainOrig   = mainVariant.price;
      const bundleOrig = bundleVariant.price;
      const totalOrig  = mainOrig + bundleOrig;
      const discCents  = Math.round(totalOrig * disc);
      const totalDisc  = totalOrig - discCents;
      const mainDisc   = mainOrig   - Math.round(mainOrig   * disc);
      const bundleDisc = bundleOrig - Math.round(bundleOrig * disc);

      this._setText('.js-single-price',      formatMoney(mainOrig));
      this._setText('.js-total-discounted',  formatMoney(totalDisc));
      this._setText('.js-total-original',    formatMoney(totalOrig));
      this._setText('.js-savings-amount',    formatMoney(discCents));
      this._setText('.js-main-disc-price',   formatMoney(mainDisc));
      this._setText('.js-main-orig-price',   formatMoney(mainOrig));
      this._setText('.js-bundle-disc-price', formatMoney(bundleDisc));
      this._setText('.js-bundle-orig-price', formatMoney(bundleOrig));
    }

    _setText(selector, text) {
      const el = this.container.querySelector(selector);
      if (el) el.textContent = text;
    }

    /* ── Utilities ── */

    _getProductForm() {
      return (
        this.container.closest('form[action*="/cart/add"]') ||
        document.querySelector('form[action="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }
  }

  /* ─────────────────────────────────────────────────────────
     Boot
  ───────────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll('.bundle-upsell').forEach(el => {
      if (el.dataset.bundleUpsellInit) return;
      el.dataset.bundleUpsellInit = '1';
      new BundleUpsell(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section reloads (theme editor live preview)
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.bundle-upsell[data-bundle-upsell-init]').forEach(el => {
      delete el.dataset.bundleUpsellInit;
    });
    init();
  });

})();
